import React from 'react';

import BetrBucks from '@/assets/icons/wallet-betr-bucks';
import { ds } from '@/components/lib/design.system';
import { Bet } from '@/feature/bets-sbk/hooks/types';
import { useFormatCurrency } from '@/hooks/use-format-currency';

import { Odds } from './Odds';
import { SocialCurrencyActiveIcon } from './SocialCurrencyActiveIcon';
import { Text, TextProps } from './TextComponent';
import { Box, BoxProps, Column, Row } from './lib/components';

type IconVariant = 'currency' | 'bonus' | 'none';

export type BetSummarySectionProps = {
    stake: number;
    multiplier?: number;
    multiplierColor?: TextProps['color'];
    strikeThroughMultiplier?: number;
    strikeThroughMultiplierColor?: TextProps['color'];
    header1Text: string;
    header1TextColor?: TextProps['color'];
    body1TextColor?: TextProps['color'];
    body1TextDecorationLine?: TextProps['textDecorationLine'];
    body1Icon?: IconVariant;
    header2Text: string;
    header2TextColor?: TextProps['color'];
    header3Text?: string;
    header3TextColor?: TextProps['color'];
    body3Text: string;
    body3TextColor?: TextProps['color'];
    /** Icon beside the payout amount: cash (default), Betr Bucks bonus, or none. */
    body3Icon?: IconVariant;
    bodyTextVariant?: TextProps['variant'];
    betSummarySeparatorColour?: BoxProps['backgroundColor'];
    betSummarySeparatorOpacity?: number;
    testID?: string;
};

export type BetSummaryProps = {
    bet: Bet;
    betSummarySectionProps?: Partial<BetSummarySectionProps>;
};

const VerticalSeparator = ({
    betSummarySeparatorColour,
    betSummarySeparatorOpacity,
}: {
    betSummarySeparatorColour: BoxProps['backgroundColor'];
    betSummarySeparatorOpacity: number;
}) => (
    <Box width={1} height={'100%'} backgroundColor={betSummarySeparatorColour} opacity={betSummarySeparatorOpacity} />
);

export const BetSummarySection = ({
    stake,
    multiplier,
    multiplierColor = ds.colorToken.text.primary,
    strikeThroughMultiplier,
    strikeThroughMultiplierColor = ds.colorToken.text.secondary,
    header1Text,
    header1TextColor = ds.colorToken.text.secondary,
    body1TextColor = ds.colorToken.text.primary,
    body1TextDecorationLine = 'none',
    body1Icon = 'currency',
    header2Text,
    header2TextColor = ds.colorToken.text.secondary,
    header3Text,
    header3TextColor = ds.colorToken.text.secondary,
    body3Text,
    body3TextColor = ds.colorToken.text.primary,
    body3Icon = 'currency',
    bodyTextVariant = 'labelLarge',
    betSummarySeparatorColour = 'neutral800',
    betSummarySeparatorOpacity = 1,
    testID,
}: BetSummarySectionProps) => {
    const { formatCurrency } = useFormatCurrency();
    return (
        <>
            <Column flex={1} alignItems="center">
                <Text variant="bodySmall" fontSize={12} color={header1TextColor} testID={`wagerHeader-${testID}`}>
                    {header1Text}
                </Text>
                <Row alignItems="center">
                    {body1Icon === 'bonus' ? <BetrBucks height={16} /> : null}
                    {body1Icon === 'currency' ? (
                        <Box mr="s4">
                            <SocialCurrencyActiveIcon />
                        </Box>
                    ) : null}
                    <Text
                        variant={bodyTextVariant}
                        color={body1TextColor}
                        textDecorationLine={body1TextDecorationLine}
                        testID={`wagerAmount-${testID}`}
                    >
                        {formatCurrency(stake)}
                    </Text>
                </Row>
            </Column>
            <VerticalSeparator
                betSummarySeparatorColour={betSummarySeparatorColour}
                betSummarySeparatorOpacity={betSummarySeparatorOpacity}
            />
            <Column flex={1} alignItems="center">
                <Text variant="bodySmall" fontSize={12} color={header2TextColor} testID={`multiplierHeader-${testID}`}>
                    {header2Text}
                </Text>
                <Row columnGap="s4" testID={`multiplierAmount-${testID}`}>
                    {strikeThroughMultiplier ? (
                        <Odds
                            variant={bodyTextVariant}
                            odds={strikeThroughMultiplier}
                            textDecorationLine="line-through"
                            color={strikeThroughMultiplierColor}
                        />
                    ) : null}
                    {multiplier ? <Odds odds={multiplier} variant={bodyTextVariant} color={multiplierColor} /> : null}
                </Row>
            </Column>
            <VerticalSeparator
                betSummarySeparatorColour={betSummarySeparatorColour}
                betSummarySeparatorOpacity={betSummarySeparatorOpacity}
            />
            <Column flex={1} alignItems="center" justifyContent="center">
                {header3Text ? (
                    <Text
                        variant="bodySmall"
                        fontSize={12}
                        color={header3TextColor}
                        textTransform="capitalize"
                        testID={`winningsHeader-${testID}`}
                    >
                        {header3Text}
                    </Text>
                ) : null}
                <Row alignItems="center" gap="s4">
                    {body3Icon === 'currency' ? <SocialCurrencyActiveIcon /> : null}
                    {body3Icon === 'bonus' ? <BetrBucks height={16} /> : null}
                    <Text variant={bodyTextVariant} color={body3TextColor} testID={`winningsAmount-${testID}`}>
                        {body3Text}
                    </Text>
                </Row>
            </Column>
        </>
    );
};
